import { z } from 'zod'

/**
 * Error contract (D-15 / R-03). Every non-2xx body is
 * `{ error: { code, message, request_id, details? } }`.
 *
 * Status rules: 404 for anything the caller can't read (no existence oracle),
 * 403 only for readable records where the action isn't permitted, 409 for
 * state conflicts, 422 for validation.
 */
export const ERROR_CATALOG = {
  // 400 / 405 / 413 / 415 transport
  bad_request: { status: 400, message: 'The request could not be read.' },
  method_not_allowed: { status: 405, message: 'Method not allowed.' },
  payload_too_large: { status: 413, message: 'The request body is too large.' },
  unsupported_media_type: { status: 415, message: 'Use application/json.' },
  // 401 authentication
  unauthenticated: { status: 401, message: 'Sign in to continue.' },
  session_expired: { status: 401, message: 'Your session has expired. Sign in again.' },
  code_invalid: { status: 401, message: 'That code is not valid.' },
  code_expired: { status: 401, message: 'That code has expired. Request a new one.' },
  oauth_failed: { status: 401, message: 'Sign-in could not be completed.' },
  // 403 authorization (readable resource, action not permitted)
  forbidden: { status: 403, message: 'You do not have permission to do that.' },
  csrf_failed: { status: 403, message: 'The request could not be verified. Refresh and retry.' },
  origin_rejected: { status: 403, message: 'Cross-origin requests are not allowed.' },
  mfa_required: { status: 403, message: 'Confirm your identity with a second factor first.' },
  impersonation_restricted: {
    status: 403,
    message: 'This action is not available while impersonating a user.',
  },
  no_access: { status: 403, message: 'This account does not have access to Fruuit Engage.' },
  org_suspended: { status: 403, message: 'This organisation is suspended.' },
  // 404
  not_found: { status: 404, message: 'Not found.' },
  // 409 state conflicts
  stale_revision: {
    status: 409,
    message: 'This record changed. Refresh and use the latest version.',
  },
  immutable: { status: 409, message: 'Published records are immutable. Create a new version.' },
  closed: { status: 409, message: 'This case is closed.' },
  duplicate: { status: 409, message: 'A matching record already exists.' },
  request_id_reused: {
    status: 409,
    message: 'This request id was already used for a different request.',
  },
  invalid_transition: { status: 409, message: 'That action is not allowed in the current state.' },
  revoked_dependency: { status: 409, message: 'Access to a required record was revoked.' },
  in_use: { status: 409, message: 'The record is referenced by other records.' },
  meeting_started: { status: 409, message: 'The meeting has already started.' },
  conflict: { status: 409, message: 'The request conflicts with the current state.' },
  // 422 validation
  validation_failed: { status: 422, message: 'Some fields are invalid.' },
  invalid_request_id: {
    status: 422,
    message: 'Send an X-Request-Id of 8-120 characters from A-Z a-z 0-9 . _ : -',
  },
  reason_required: { status: 422, message: 'Record a substantive reason (8-12000 characters).' },
  limit_reached: { status: 422, message: 'The limit for this item has been reached.' },
  // 429
  rate_limited: { status: 429, message: 'Too many requests. Try again shortly.' },
  code_locked: { status: 429, message: 'Too many attempts. Try again later.' },
  resend_cooldown: { status: 429, message: 'Wait a moment before requesting another code.' },
  // 5xx
  internal_error: { status: 500, message: 'Something went wrong. Nothing was saved.' },
  config_missing: { status: 503, message: 'The service is not configured. Nothing was saved.' },
  email_unavailable: { status: 503, message: 'Email sign-in is temporarily unavailable.' },
  provider_unavailable: { status: 503, message: 'This sign-in provider is not available.' },
  db_unavailable: { status: 503, message: 'The database is temporarily unavailable.' },
} as const satisfies Record<string, { status: number; message: string }>

export type ErrorCode = keyof typeof ERROR_CATALOG

export const ERROR_CODES = Object.keys(ERROR_CATALOG) as [ErrorCode, ...ErrorCode[]]

export const errorBodySchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    request_id: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
})

export type ErrorBody = z.infer<typeof errorBodySchema>

export function errorStatus(code: ErrorCode): number {
  return ERROR_CATALOG[code].status
}

export function errorBody(
  code: ErrorCode,
  requestId: string,
  options: { message?: string; details?: Record<string, unknown> } = {},
): ErrorBody {
  const error: ErrorBody['error'] = {
    code,
    message: options.message ?? ERROR_CATALOG[code].message,
    request_id: requestId,
  }
  if (options.details !== undefined) error.details = options.details
  return { error }
}
